/**
 * This module exports Scalyr-defined pure functions related to accounts, such
 * as labs, settings, etc.
 */

import { TeamData } from 'common/graphqlTypes';
import { LC_EX, NUM_EX, PASSWORD_STRENGTH, SY_EX, UC_EX } from 'common/constants';

export const calcPasswordStrength = (password: string): PASSWORD_STRENGTH => {
  if (password.length === 0) {
    return PASSWORD_STRENGTH.UNDEFINED;
  }
  if (password.length < 6) {
    return PASSWORD_STRENGTH.INVALID;
  }

  let characterSetSize = 0;

  // loop over each char of the password and check it per regexes above.
  // weight numbers, upper case and lowercase at .75, 1 and .25 respectively.
  if (NUM_EX.test(password)) {
    characterSetSize += 10;
  }
  if (UC_EX.test(password)) {
    characterSetSize += 26;
  }
  if (LC_EX.test(password)) {
    characterSetSize += 26;
  }
  if (SY_EX.test(password)) {
    characterSetSize += 32;
  }

  // assume that 100% is a meterMult of maxMulti
  const strength = characterSetSize ** password.length;

  // init crackers at hashes/second
  // all numbers from slowest computer here http://hashcat.net/oclhashcat-plus/
  const rateBcrypt = 604;
  const bcrypt = strength / rateBcrypt;

  if (bcrypt < 86400 * 365) {
    return PASSWORD_STRENGTH.WEAK;
  }
  if (bcrypt < 86400 * 365 * 1000000) {
    return PASSWORD_STRENGTH.MODERATE;
  }
  return PASSWORD_STRENGTH.STRONG;
};

/**
 * Get the preferred display name for a team. Prefers name over email address.
 *
 * @param team    a team
 * @returns name to display
 */
export const getTeamDisplayName = (team: TeamData) => team?.name || team?.emailAddress || '';
